/**
 * Central registry of all PostHog custom event names.
 *
 * This is the single source of truth for event names sent to PostHog.
 * Using `as const` collapses each value to its literal string type so
 * TypeScript can enforce valid event names at call sites without runtime cost.
 *
 * To add a new event: add an entry here, then use `AnalyticsEvents.YOUR_KEY`
 * in the component. Never pass raw string literals to `track()`.
 */
export const AnalyticsEvents = {
  ARTIFACT_DELIVERY: 'artifact_delivery',
  EARLY_QUICK_SUMMARY_RECEIVED: 'early_quick_summary_received',
  ARTIFACT_PIPELINE_TIMING: 'artifact_pipeline_timing',

  // ── Sign-in flow ─────────────────────────────────────────────────────────
  SIGNIN_EMAIL_SUBMITTED: 'signin_email_submitted',
  SIGNIN_OTP_SUBMITTED: 'signin_otp_submitted',
  SIGNIN_OTP_FAILED: 'signin_otp_failed',
  SIGNIN_COMPLETED: 'signin_completed',

  // ── Sign-up flow ─────────────────────────────────────────────────────────
  SIGNUP_EMAIL_SUBMITTED: 'signup_email_submitted',
  SIGNUP_OTP_SUBMITTED: 'signup_otp_submitted',
  SIGNUP_OTP_FAILED: 'signup_otp_failed',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_CHANGE_EMAIL_CLICKED: 'signup_change_email_clicked',
  SIGNUP_BACK_NAVIGATED: 'signup_back_navigated',
  SIGNUP_GOOGLE_CLICKED: 'signup_google_clicked',
  EMBEDDED_BROWSER_GUARD_SHOWN: 'embedded_browser_guard_shown',
  SIGNUP_NAME_GATE_VIEWED: 'signup_name_gate_viewed',
  SIGNUP_NAME_GATE_SUBMITTED: 'signup_name_gate_submitted',
  RESEND_AUDIENCE_MEMBERSHIP: 'resend_audience_membership',

  // ── Form validation (cross-flow) ────────────────────────────────────────
  FORM_VALIDATION_FAILED: 'form_validation_failed',

  // ── Onboarding ────────────────────────────────────────────────────────────
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  ONBOARDING_COMPLETED: 'onboarding_completed',

  // ── Storyworth campaign funnel (distinct from onboarding_* ) ───────────────
  CAMPAIGN_FUNNEL_ENTERED: 'campaign_funnel_entered',
  CAMPAIGN_UPLOAD_STARTED: 'campaign_upload_started',
  CAMPAIGN_PARSE_COMPLETED: 'campaign_parse_completed',
  CAMPAIGN_SIGNUP_COMPLETED: 'campaign_signup_completed',
  CAMPAIGN_REVEAL_VIEWED: 'campaign_reveal_viewed',

  // Post-reveal paywall (Plan 3) — keyed on collection_id, NOT attempt_id: these
  // fire on the standalone reveal route, which has no anon-funnel attempt_id.
  CAMPAIGN_PAYWALL_VIEWED: 'campaign_paywall_viewed',
  CAMPAIGN_SUBSCRIBE_STARTED: 'campaign_subscribe_started',
  CAMPAIGN_PEOPLE_SUBSCRIBE_CLICKED: 'campaign_people_subscribe_clicked',

  // ── Storyworth document import (fires on BOTH upload surfaces) ────────────
  DOCUMENT_REPRINT_WARNING_SHOWN: 'document_reprint_warning_shown',
  DOCUMENT_NO_ENTRIES_SHOWN: 'document_no_entries_shown',
  // Core (non-campaign) biographies only: the Documents tab refused an upload.
  DOCUMENT_REFUSED_SHOWN: 'document_refused_shown',

  // ── Biography creation ───────────────────────────────────────────────────
  BIOGRAPHY_CREATION_STARTED: 'biography_creation_started',
  NEW_BIOGRAPHY_CREATED: 'new_biography_created',

  // ── Contributor invite flow ───────────────────────────────────────────────
  CONTRIBUTOR_INVITE_OPENED: 'contributor_invite_opened',
  CONTRIBUTOR_NAME_SUBMITTED: 'contributor_name_submitted',
  CONTRIBUTOR_DISCLOSURE_ACKNOWLEDGED: 'contributor_disclosure_acknowledged',
  CONTRIBUTOR_TOUR_CHOICE_MADE: 'contributor_tour_choice_made',
  CONTRIBUTOR_INTRO_COMPLETED: 'contributor_intro_completed',

  // ── Feature engagement ────────────────────────────────────────────────────
  BIOGRAPHY_SECTION_OPENED: 'biography_section_opened',
  INVITE_FLOW_STARTED: 'invite_flow_started',
  INVITE_EMAILS_SENT: 'invite_emails_sent',
  INVITE_LINK_CREATED: 'invite_link_created',
  INVITE_LINK_SHARED: 'invite_link_shared',
  CHECKOUT_STARTED: 'checkout_started',
  BILLING_PORTAL_OPENED: 'billing_portal_opened',
  SECOND_BIOGRAPHY_GATED: 'second_biography_gated',
  SUPPORT_LINK_OPENED: 'support_link_opened',
  SUPPORT_LINK_ACTION: 'support_link_action',

  // ── Conversation lifecycle ────────────────────────────────────────────────
  SPEECH_CONVERSATION_STARTED: 'speech_conversation_started',
  SPEECH_MESSAGE_SENT: 'speech_message_sent',
  VOICE_START_FAILED: 'voice_start_failed',
  VOICE_START_TIMING: 'voice_start_timing',
  MIC_RECOVERY_RESOLVED: 'mic_recovery_resolved',
  MIC_SETUP_PROBLEM: 'mic_setup_problem',
  TEXT_CONVERSATION_STARTED: 'text_conversation_started',
  TEXT_MESSAGE_SENT: 'text_message_sent',
  CONVERSATION_GUARDRAIL_TRIGGERED: 'conversation_guardrail_triggered',
  CONVERSATION_STARTED: 'conversation_started',
  CONVERSATION_CONNECTED: 'conversation_connected',
  CONVERSATION_DISCONNECTED: 'conversation_disconnected',
  CONVERSATION_COMPLETED: 'conversation_completed',
  CONVERSATION_METADATA_RECEIVED: 'conversation_metadata_received',
  INTAKE_PACING_OVERRUN_PING: 'intake_pacing_overrun_ping',
  INTAKE_AI_DISCLOSURE_SHOWN: 'intake_ai_disclosure_shown',
  INTAKE_AI_DISCLOSURE_ACKNOWLEDGED: 'intake_ai_disclosure_acknowledged',
  INTAKE_COMPLETED: 'intake_completed',

  // ── Speaker attribution measurement (Phase 1) ──────────────────────────
  SPEAKER_SUGGESTED: 'speaker_suggested',
  SPEAKER_CONFIRMED: 'speaker_confirmed',
  SPEAKER_MAPPED: 'speaker_mapped',

  // ── Fact validation before Zep write (#2813) ──────────────────────────
  NOTE_FACTS_VALIDATED: 'note_facts_validated',
  NOTE_FABRICATED_NAMES_DROPPED: 'note_fabricated_names_dropped',
  NOTE_TEXT_FIELD_CLAMPED: 'note_text_field_clamped',

  // ── Memory grounding check (#4799) ─────────────────────────────────────
  MEMORY_GROUNDING_REVIEWED: 'memory_grounding_reviewed',

  // ── Substance gate ─────────────────────────────────────────────────────
  CONVERSATION_SKIPPED_NO_SUBSTANCE: 'conversation_skipped_no_substance',

  // ── Share-a-memory entry chooser ──────────────────────────────────────────
  SHARE_MEMORY_CHOOSER_OPENED: 'share_memory_chooser_opened',
  SHARE_MEMORY_CHOOSER_PICKED: 'share_memory_chooser_picked',
  SHARE_MEMORY_CHOOSER_DISMISSED: 'share_memory_chooser_dismissed',

  // ── Biography settings ─────────────────────────────────────────────────────
  AUDIO_PLAYBACK_TOGGLED: 'audio_playback_toggled',

  // ── Billing / Stripe lifecycle ────────────────────────────────────────────
  CANCEL_INTERCEPT_SHOWN: 'cancel_intercept_shown',
  CANCEL_INTERCEPT_DISMISSED: 'cancel_intercept_dismissed',
  CANCEL_INTERCEPT_ACCEPTED: 'cancel_intercept_accepted',
  SUBSCRIPTION_CHECKOUT_COMPLETED: 'subscription_checkout_completed',
  SUBSCRIPTION_CREATED: 'subscription_created',
  SUBSCRIPTION_TRIAL_CONVERTED: 'subscription_trial_converted',
  SUBSCRIPTION_CANCELLATION_SCHEDULED: 'subscription_cancellation_scheduled',
  SUBSCRIPTION_CANCELLATION_REVERSED: 'subscription_cancellation_reversed',
  SUBSCRIPTION_PAYMENT_PAST_DUE: 'subscription_payment_past_due',
  SUBSCRIPTION_PAYMENT_RECOVERED: 'subscription_payment_recovered',
  SUBSCRIPTION_PAUSED: 'subscription_paused',
  SUBSCRIPTION_RENEWED: 'subscription_renewed',
  SUBSCRIPTION_STATUS_CHANGED: 'subscription_status_changed',
  SUBSCRIPTION_DELETED: 'subscription_deleted',
  SUBSCRIPTION_TRIAL_ENDING_SOON: 'subscription_trial_ending_soon',
  SUBSCRIPTION_DISCOUNT_APPLIED: 'subscription_discount_applied',
  INVOICE_PAID: 'invoice_paid',
  INVOICE_PAYMENT_FAILED: 'invoice_payment_failed',

  // ── Server-only domains (from posthog-server.ts) ─────────────────────
  CONVERSATION_CONTEXT_EXPLICIT: 'conversation_context_explicit',
  CONVERSATION_CONTEXT_INFERRED: 'conversation_context_inferred',
  CONVERSATION_CONTEXT_NONE: 'conversation_context_none',
  CONVERSATION_VALIDATION_FAILED: 'conversation_validation_failed',
  CONVERSATION_ARCHIVED_BLOCKED: 'conversation_archived_blocked',
  CONVERSATION_UNAUTHORIZED_BLOCKED: 'conversation_unauthorized_blocked',
  GREETING_PATTERN_SELECTED: 'greeting_pattern_selected',
  THEME_REDIRECT_TRIGGERED: 'theme_redirect_triggered',
  EMOTIONAL_VALIDATION_USED: 'emotional_validation_used',
  COLLECTION_THEME_MENTIONED: 'collection_theme_mentioned',
  FEEDBACK_SUBMITTED: 'feedback_submitted',

  // ── Phase 2: Email pipeline ─────────────────────────────────────────
  EMAIL_SEND_REQUESTED: 'email_send_requested',
  EMAIL_SEND_FAILED: 'email_send_failed',
  EMAIL_SEND_SKIPPED: 'email_send_skipped',
  EMAIL_DELIVERED: 'email_delivered',
  EMAIL_OPENED: 'email_opened',
  EMAIL_CLICKED: 'email_clicked',
  EMAIL_BOUNCED: 'email_bounced',
  EMAIL_COMPLAINED: 'email_complained',
  EMAIL_DELIVERY_DELAYED: 'email_delivery_delayed',

  // ── Phase 2: Milestones and trial automation ──────────────────────────
  MILESTONE_REACHED: 'milestone_reached',
  CREATOR_DRIP_STAGE_OUTCOME: 'creator_drip_stage_outcome',
  REENGAGEMENT_DRIP_STAGE_OUTCOME: 'reengagement_drip_stage_outcome',
  CONTRIBUTOR_DRIP_STAGE_OUTCOME: 'contributor_drip_stage_outcome',
  TRIAL_AUTOMATION_STARTED: 'trial_automation_started',
  TRIAL_AUTOMATION_CANCELLED: 'trial_automation_cancelled',
  TRIAL_REMINDER_STAGE_OUTCOME: 'trial_reminder_stage_outcome',
  AUTH_USER_ALIASED_RECIPIENT: 'auth_user_aliased_recipient',
  TOPIC_OUTREACH_CALL_ATTEMPT_OUTCOME: 'topic_outreach_call_attempt_outcome',
  TOPIC_OUTREACH_CALL_FELL_BACK_TO_EMAIL: 'topic_outreach_call_fell_back_to_email',

  // ── Phase 3: Content consumption + lifecycle events ────────────────────
  CHAPTER_VIEWED: 'chapter_viewed',
  CHAPTER_PDF_DOWNLOADED: 'chapter_pdf_downloaded',
  BIOGRAPHY_CREATED_SERVER: 'biography_created_server',
  BIOGRAPHY_ARCHIVED: 'biography_archived',
  BIOGRAPHY_UNARCHIVED: 'biography_unarchived',
  PHOTO_UPLOADED: 'photo_uploaded',
  PHOTO_ANALYZED: 'photo_analyzed',
  SLIDESHOW_OPENED: 'slideshow_opened',
  SLIDESHOW_CLOSED: 'slideshow_closed',
  PRODUCER_NOTES_CREATED: 'producer_notes_created',
  PRODUCER_NOTES_PUBLISHED: 'producer_notes_published',
  PRODUCER_NOTES_VIEWED: 'producer_notes_viewed',
  MEMBERSHIP_ADDED: 'membership_added',
  MEMBERSHIP_REMOVED: 'membership_removed',
  ACCOUNT_DELETED: 'account_deleted',

  // ── Biography timeline ────────────────────────────────────────────────
  GAP_CARD_CLICKED: 'gap_card_clicked',
  GAP_CARD_EXPANDED: 'gap_card_expanded',
  TIMELINE_MEMORY_CLICKED: 'timeline_memory_clicked',

  // ── Biography notes ───────────────────────────────────────────────────
  NOTE_FOLLOWUP_CLICKED: 'note_followup_clicked',

  // ── Biography people ──────────────────────────────────────────────────
  PERSON_INVITATION_CARD_CLICKED: 'person_invitation_card_clicked',

  // ── Phase 3: AI agent telemetry ────────────────────────────────────────
  AI_AGENT_RUN_STARTED: 'ai_agent_run_started',
  AI_AGENT_RUN_COMPLETED: 'ai_agent_run_completed',
  AI_AGENT_RUN_FAILED: 'ai_agent_run_failed',
  AI_AGENT_RATE_LIMITED: 'ai_agent_rate_limited',
  AI_EVAL_RUN: 'ai_eval_run',
  INTERVIEWER_CALL_EVAL: 'interviewer_call_eval',
  LORI_VOICE_FIDELITY: 'lori_voice_fidelity',

  // ── Era-context producer funnel ────────────────────────────────────────
  ERA_CONTEXT_PRODUCER_EXPOSED: 'era_context_producer_exposed',
  ERA_CONTEXT_REVIEW_COMPLETED: 'era_context_review_completed',
  ERA_CONTEXT_CHAPTER_EXPOSED: 'era_context_chapter_exposed',
  ERA_CONTEXT_CHAPTER_DECLARED: 'era_context_chapter_declared',

  // ── Chapter writer: revision seeded (#5078) ────────────────────────────
  CHAPTER_REVISION_SEEDED: 'chapter_revision_seeded',

  // ── Chapter writer: epigraph dropped at finalization (#4650) ───────────
  CHAPTER_EPIGRAPH_DROPPED: 'chapter_epigraph_dropped',

  // ── Chapter writer: narrative-flagged units removed at acceptance (#4887) ──
  CHAPTER_NARRATIVE_SENTENCES_REMOVED: 'chapter_narrative_sentences_removed',

  // ── People-view name edits (#5000) ─────────────────────────────────────
  PERSON_NAME_EDITED: 'person_name_edited',
  FACT_CORRECTED: 'fact_corrected',

  // ── Producer coverage tripwire (#2826) ──────────────────────────────────
  CHAPTER_COVERAGE_STALL: 'chapter_coverage_stall',

  // ── Phone-call webhook ─────────────────────────────────────────────────
  PHONE_CALL_RATE_LIMITED: 'phone_call_rate_limited',

  // ── Contributor nudge ─────────────────────────────────────────────────
  NUDGE_SENT: 'nudge_sent',
  NUDGE_ABANDONED: 'nudge_abandoned',

  // ── Book & print orders ────────────────────────────────────────────────
  BOOK_PAGE_VIEWED: 'book_page_viewed',
  BOOK_NOT_READY_CTA_CLICKED: 'book_not_ready_cta_clicked',
  BOOK_PREVIEW_OPENED: 'book_preview_opened',
  BOOK_ORDER_STARTED: 'book_order_started',
  BOOK_ORDER_COMPLETED: 'book_order_completed',
  BOOK_ORDER_FAILED: 'book_order_failed',

  // ── Quote audio ────────────────────────────────────────────────────────
  QUOTE_AUDIO_VOICES_TAB_OPENED: 'quote_audio_voices_tab_opened',
  QUOTE_AUDIO_PLAY_STARTED: 'quote_audio_play_started',
  QUOTE_AUDIO_PLAY_COMPLETED: 'quote_audio_play_completed',
  QUOTE_AUDIO_PLAY_FAILED: 'quote_audio_play_failed',

  // ── Infrastructure / resilience ────────────────────────────────────────
  CHUNK_LOAD_RELOAD: 'chunk_load_reload',

  // Marketing subscribe
  SUBSCRIBE_SUBMITTED: 'subscribe_submitted',
  SUBSCRIBE_CONFIRMED: 'subscribe_confirmed',
  SUBSCRIBE_FAILED: 'subscribe_failed',
  SUBSCRIBE_MODAL_SHOWN: 'subscribe_modal_shown',
  SUBSCRIBE_MODAL_DISMISSED: 'subscribe_modal_dismissed',

  // Marketing guides
  GUIDE_TEMPLATE_DOWNLOADED: 'guide_template_downloaded',

  // ── Welcome banner + tour (biography home) ───────────────────────────────
  WELCOME_BANNER_SHOWN: 'welcome_banner_shown',
  WELCOME_BANNER_DISMISSED: 'welcome_banner_dismissed',
  WELCOME_TOUR_OPENED: 'welcome_tour_opened',
  WELCOME_TOUR_STEP_VIEWED: 'welcome_tour_step_viewed',
  WELCOME_TOUR_PAUSED: 'welcome_tour_paused',
  WELCOME_TOUR_CLOSED: 'welcome_tour_closed',

  // ── Post-first-conversation Home block ──────────────────────────────────
  HOME_FIRST_CONVERSATION_VIEWED: 'home_first_conversation_viewed',

  // ── Valued paywall ───────────────────────────────────────────────────────
  VALUE_PAYWALL_SHOWN: 'value_paywall_shown',
  VALUE_PAYWALL_CHECKOUT_STARTED: 'value_paywall_checkout_started',
  VALUE_PAYWALL_DECK_SCROLLED: 'value_paywall_deck_scrolled',
  VALUE_PAYWALL_SAMPLE_SHOWN: 'value_paywall_sample_shown',

  // ── Meet Lori (v2 onboarding intake capture) ──────────────────────────────
  MEET_LORI_CARD_SHOWN: 'meet_lori_card_shown',
  MEET_LORI_CLOSED: 'meet_lori_closed',
  MEET_LORI_CHOOSER_VIEWED: 'meet_lori_chooser_viewed',
  MEET_LORI_CHOOSER_SELECTED: 'meet_lori_chooser_selected',

  // ── Memory cards ──────────────────────────────────────────────────────────
  MEMORY_CARD_CREATED: 'memory_card_created',
  MEMORY_CARD_VIEWED: 'memory_card_viewed',
  MEMORY_CARD_REVOKED: 'memory_card_revoked',

  // ── Chapter cards ─────────────────────────────────────────────────────────
  CHAPTER_CARD_CREATED: 'chapter_card_created',
  CHAPTER_CARD_VIEWED: 'chapter_card_viewed',
  CHAPTER_CARD_REVOKED: 'chapter_card_revoked',
  CHAPTER_CARD_REFRESHED: 'chapter_card_refreshed',

  // ── Ops monitoring ────────────────────────────────────────────────────────
  ELEVENLABS_BILLING_SNAPSHOT: 'elevenlabs_billing_snapshot',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// Re-export the typed manifest as the canonical reference.
export type { EventManifest, EventManifestKey, EventName, EventProps } from './manifest';
